import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState, type FC } from "react";
import { toast } from "sonner";

import { composeDocx } from "@/api/drafts-v2";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText
} from "@/components/ui/input-group";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { triggerDownload } from "@/helpers/file_helpers";
import { useEntitlements } from "@/hooks/useEntitlements";
import { Feature } from "@/types/entitlements";

import { useCaseIdeOptional } from "../../context/CaseIdeContext";
import { splitExportTitle, withAbsoluteLinks } from "../replyExport";
import { useSaveReplyToWorkProducts } from "../useSaveReplyToWorkProducts";

type Format = "docx" | "pdf";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The reply's markdown. */
  markdown: string;
  /** The host is a case chat, so the reply can go to Work Products. */
  canSaveToWorkProducts?: boolean;
};

const FieldLabel: FC<{ children: string; htmlFor?: string }> = ({
  children,
  htmlFor
}) => (
  <label htmlFor={htmlFor} className="text-sm font-medium text-fg-2">
    {children}
  </label>
);

/**
 * Export a reply as Word or PDF, and in a case chat also save it to Work
 * Products as an editable draft. The download and the save run side by side;
 * whichever succeeded is not redone when the user retries the other.
 */
export const ExportReplyDialog: FC<Props> = ({
  open,
  onOpenChange,
  markdown,
  canSaveToWorkProducts = false
}) => {
  const ide = useCaseIdeOptional();
  const saveReply = useSaveReplyToWorkProducts();
  const { hasFeature } = useEntitlements();
  const canSave =
    canSaveToWorkProducts && Boolean(saveReply) && hasFeature(Feature.DRAFTING);

  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [format, setFormat] = useState<Format>("docx");
  const [save, setSave] = useState(false);
  const [working, setWorking] = useState(false);
  const done = useRef({ exported: false, saved: false });

  useEffect(() => {
    if (!open) return;
    const split = splitExportTitle(markdown);
    setName(split.title);
    setBody(split.body);
    setFormat("docx");
    setSave(false);
    done.current = { exported: false, saved: false };
  }, [open, markdown]);

  const wantSave = canSave && save;

  const run = async () => {
    const title = name.trim();
    if (!title) return;
    setWorking(true);
    const text = withAbsoluteLinks(body);
    const [exported, saved] = await Promise.allSettled([
      done.current.exported
        ? Promise.resolve()
        : composeDocx(text, format, title, title).then(({ bytes, filename }) =>
            triggerDownload(bytes, filename, format)
          ),
      wantSave && !done.current.saved && saveReply
        ? saveReply({ title, body: text })
        : Promise.resolve(null)
    ]);
    setWorking(false);

    if (exported.status === "fulfilled") {
      done.current.exported = true;
    } else {
      Sentry.captureException(exported.reason);
      toast.error("Couldn't export the response.");
    }
    if (saved.status === "fulfilled") {
      if (saved.value) {
        done.current.saved = true;
        const { draftId } = saved.value;
        toast.success(`Saved ${title} to Work Products`, {
          action: {
            label: "Open",
            onClick: () => ide?.open({ kind: "draft", id: draftId, title })
          }
        });
      }
    } else {
      Sentry.captureException(saved.reason);
      toast.error("Couldn't save to Work Products.");
    }
    if (exported.status === "fulfilled" && saved.status === "fulfilled") {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={working ? undefined : onOpenChange}>
      <DialogContent
        className="grid-cols-1 gap-0 p-0 sm:max-w-110"
        data-testid="export-reply"
      >
        <DialogHeader className="gap-1 px-5 pt-4">
          <DialogTitle className="text-md">Export Response</DialogTitle>
          <DialogDescription className="text-sm">
            Download this response as a document.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3.5 px-5 pt-3.5 pb-4">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="export-reply-name">Name</FieldLabel>
            <InputGroup>
              <InputGroupInput
                id="export-reply-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <InputGroupAddon align="inline-end">
                <InputGroupText className="font-normal text-fg-4">
                  .{format}
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel>Format</FieldLabel>
            <Tabs
              value={format}
              onValueChange={(value) =>
                setFormat(value === "pdf" ? "pdf" : "docx")
              }
            >
              <TabsList variant="segmented" className="w-fit">
                <TabsTrigger value="docx" className="px-2.5">
                  Word (.docx)
                </TabsTrigger>
                <TabsTrigger value="pdf" className="px-2.5">
                  PDF (.pdf)
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {canSave ? (
            <>
              <div className="border-t border-line-1" />
              <div className="flex items-center gap-3">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-base text-fg">
                    Also Save to Work Products
                  </span>
                  <span className="text-sm text-fg-3">
                    Adds an editable Word draft to this case.
                    {save && format === "pdf"
                      ? " The download is a PDF; the saved copy stays editable."
                      : null}
                  </span>
                </div>
                <Switch
                  checked={save}
                  onCheckedChange={setSave}
                  aria-label="Also Save to Work Products"
                />
              </div>
            </>
          ) : null}
        </div>
        <DialogFooter className="mx-0 mb-0 px-5 py-3">
          <Button
            variant="ghost"
            disabled={working}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || working}
            onClick={() => void run()}
            data-testid="export-reply-submit"
          >
            {working ? <Spinner className="size-3.5" /> : null}
            {wantSave ? "Export and Save" : "Export"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
